import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  TextField,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { FORM_STYLES } from "../../constants";
import TooltipWithClickAway from "../terminalPicker/TooltipWithClickAway";
import { CreateMSDNotes } from "../../API/CreateMSDNotes";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { REQUIRED_MSG, SELECT_MSG } from "../../schema/formik";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";
import { MisDeliveryStatus } from "../../models";

interface F18NoteSectionProps {
  f18ProNumbersOptions?: number[];
  exceptionNumber?: number;
  setLoading: (loading: boolean) => void;
  setFormMode: (mode: string) => void;
  setF18Note: (changed: string) => void;
  setF18NoteProNumbers: (changed: number[]) => void;
  f18Note: string;
  f18NoteProNumbers: number[];
  investigationStatus?: MisDeliveryStatus;
}

const F18NoteSection: React.FC<F18NoteSectionProps> = ({
  f18ProNumbersOptions,
  exceptionNumber,
  setLoading,
  setFormMode,
  f18Note,
  f18NoteProNumbers,
  setF18Note,
  setF18NoteProNumbers,
  investigationStatus,
}) => {
  const dispatch = useDispatch();

  const [note, setNote] = useState("");
  const [proNumbers, setProNumbers] = useState<number[]>([]);
  const [disabled, setDisabled] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [proTouched, setProTouched] = useState(false);
  const [noteTouched, setNoteTouched] = useState(false);

  /** Old values stored in ref (no re-render) */
  const oldNoteRef = useRef("");
  const oldProNumbersRef = useRef<number[]>([]);

  /** Change detection (FAST) */
  const isChanged = useMemo(() => {
    if (note !== oldNoteRef.current) return true;

    const oldPros = oldProNumbersRef.current;
    if (proNumbers.length !== oldPros.length) return true;

    for (let i = 0; i < proNumbers.length; i++) {
      if (proNumbers[i] !== oldPros[i]) return true;
    }
    return false;
  }, [note, proNumbers]);

  /** Validation */
  const isInvalid = useMemo(
    () => note.trim() === "" || proNumbers.length === 0,
    [note, proNumbers.length],
  );

  const proError = proTouched && proNumbers.length === 0;
  const noteError = noteTouched && note.trim() === "";

  /** Dispatch form changed flag */
  useEffect(() => {
    dispatch(isFormValueChanged(isChanged));
  }, [dispatch, isChanged]);

  useEffect(() => {
    if (!f18Note && f18NoteProNumbers.length === 0) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
    setNote(f18Note);
    setProNumbers(f18NoteProNumbers);
    oldNoteRef.current = f18Note;
    oldProNumbersRef.current = f18NoteProNumbers;
  }, [f18Note, f18NoteProNumbers]);

  /** Handlers */
  const handleSubmit = useCallback(async () => {
    if (!isChanged) {
      return;
    }
    if (!exceptionNumber || isInvalid) {
      setProTouched(true);
      setNoteTouched(true);
      return;
    }

    try {
      setLoading(true);
      await CreateMSDNotes({
        exceptionNumber,
        proNumbers,
        note,
      });
      setF18Note(note);
      setF18NoteProNumbers(proNumbers);
      setFormMode("view");
      oldNoteRef.current = note;
      oldProNumbersRef.current = proNumbers;
      setDisabled(true);
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.SUCCESS,
          "MSD Notes created successfully",
          Date.now().toString(),
        ),
      );
    } catch (error) {
      console.error("CreateMSDNotes failed", error);
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.ERROR,
          typeof error === "string" ? error : "Failed to create MSD Notes",
          Date.now().toString(),
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [
    exceptionNumber,
    isInvalid,
    note,
    proNumbers,
    setFormMode,
    setLoading,
    dispatch,
    setF18Note,
    setF18NoteProNumbers,
    isChanged,
  ]);

  return (
    <>
      <Grid container spacing={2} className="filter-margin">
        <Grid item md={3} xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel
              htmlFor={"proNumber"}
              style={FORM_STYLES.LABEL}
              shrink={true}
            >
              Pro# <sup>*</sup>
            </InputLabel>
            <Autocomplete
              id={"proNumber"}
              key={`search-pro-number`}
              disablePortal
              className="custom-autocomplete"
              multiple
              disabled={disabled}
              forcePopupIcon={false}
              getOptionLabel={(option) => option?.toString()}
              disableCloseOnSelect
              value={proNumbers}
              style={FORM_STYLES.LABEL}
              options={f18ProNumbersOptions ?? []}
              onChange={(e, v) => {
                setProNumbers(v);
                setInputValue("");
                setProTouched(true);
              }}
              inputValue={inputValue}
              onClose={(_e) => {
                setInputValue("");
              }}
              onInputChange={(_, v, r) => r === "input" && setInputValue(v)}
              renderOption={(optionProps, option) => {
                // MUI includes a `key` on the props object; React warns if it is spread.
                const { key, ...liProps } = optionProps as any;

                return (
                  <li key={key} {...liProps} title={option.toString()}>
                    <Checkbox checked={proNumbers.includes(option)} />
                    {option}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  autoComplete="off"
                  {...params}
                  variant="outlined"
                  className="test"
                  onBlur={() => setProTouched(true)}
                  error={proError}
                />
              )}
              // renderTags={renderTags}
              renderTags={(value) => (
                <div
                  className="auto-complete-selected-container"
                  title={value.join(", ")}
                >
                  {value.join(", ")}
                </div>
              )} // Add renderTags property to customize rendering of selected values
            />
          </FormControl>
          {proError && <span className="form-error">{SELECT_MSG}</span>}
        </Grid>
        <Grid item md={9} xs={12}>
          <FormControl fullWidth size="small" className="f18-misdel">
            <InputLabel style={FORM_STYLES.LABEL} shrink={true}>
              Addition Note<sup>*</sup>
              &nbsp;
              <TooltipWithClickAway
                tooltipText={"This will map to the F18 notes in the AS400"}
                tooltipKey={"additionalNotes"}
              />
            </InputLabel>
            <TextField style={{ marginTop: '-4px' }}
              disabled={disabled}
              autoComplete="off"
              id="additionNote"
              placeholder={"Enter details"}
              variant="outlined"
              name="additionNote"
              data-testid="additionNote"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => setNoteTouched(true)}
              inputProps={{ maxLength: 300 }}
              error={noteError}
              helperText={noteError ? REQUIRED_MSG : ""}
            />
          </FormControl>
        </Grid>
      </Grid>
      {investigationStatus !== MisDeliveryStatus.investigationComplete && (
        <div className="btn-group-submit">
          {!disabled && (
            <>
              {f18Note ? (
                <Button
                  type="button"
                  variant="contained"
                  data-testid="f18-submit"
                  className={
                    isChanged
                    ? "assign mr-0 ml"
                      : " assign mr-0 ml disable-btn disable"
                  }
                  onClick={handleSubmit}
                >
                  Update F18 Note
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="contained"
                  data-testid="f18-submit"
                  className={
                    isChanged ?  "entry ml" : " entry ml disable-btn disable" 
                  }
                  onClick={handleSubmit}
                >
                  Save
                </Button>
              )}

              <Button
                type="button"
                variant="contained"
                data-testid="f18-cancel"
                className="clear-btn mr-0 ml"
                onClick={() => {
                  setNote(oldNoteRef.current);
                  setProNumbers(oldProNumbersRef.current);
                  setInputValue("");
                  setNoteTouched(false);
                  setProTouched(false);
                }}
                // disabled={isSubmitting}
              >
                Cancel
              </Button>
            </>
          )}
          {disabled && (
            <Button
              type="button"
              variant="contained"
              data-testid="f18-edit"
              className="assign mr-0 ml"
              onClick={() => {
                setDisabled(false);
              }}
            >
              Edit F18 Note
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default F18NoteSection;
